import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { roleMatches } from '../utils/rolePermissions';
import { hasGrantModuleAccess, STORE_TAB_KEYS, hasGrantTabAccess } from '../utils/viewPermissions';
import StoreDashboard from './store/StoreDashboard';
import StoreWorkOrderRequests from './store/StoreWorkOrderRequests';
import StoreWorkOrderRequirements from './store/StoreWorkOrderRequirements';
import ReconciliationModule from './store/ReconciliationModule';
import ToolControl from './store/ToolControl';
import StorePartsTab from './store/StorePartsTab';
import AccessLogModule from './store/AccessLogModule';
import LoanItemsModule from './store/LoanItemsModule';
import WorkflowGuide from './common/WorkflowGuide';

/** Current Store Activities tab indices (Receiving lives on /receiving-and-inspection). */
export const STORE_TAB = {
  DASHBOARD: 0,
  INTENT: 1,
  RECONCILIATION: 2,
  TOOL_CONTROL: 3,
  PARTS: 4,
  ACCESS_LOGS: 5,
  LOAN_ITEMS: 6,
  WORK_ORDER_REQUIREMENTS: 7
};

const Store = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [linkState, setLinkState] = useState({ recordId: null, recordType: null });
  const { user, viewGrants } = useAuth();

  useLayoutEffect(() => {
    const path = location.pathname || '';
    if (path === '/store/receiving' || path === '/store/receving') {
      navigate('/receiving-and-inspection', {
        replace: true,
        state: location.state || null
      });
      return;
    }

    const state = location.state || {};
    const t = state.tab;
    const recordType = state.recordType;

    // Issuance was removed from Store Activities earlier —
    // keep redirect for any remaining tab:4 deep links without a returns recordType.
    if (t === 4 && recordType !== 'reconciliation' && recordType !== 'issuance' && !state.recordId) {
      // Ambiguous legacy issuance index; prefer very old "Issuance tab" only when not a returns deep-link.
      // Returns deep-links use recordType + recordId; issuance redirects use /issuance directly elsewhere.
    }

    if (recordType === 'issuance' && !state.stayOnStoreReturns) {
      navigate('/issuance', {
        replace: true,
        state: {
          recordId: state.recordId || null,
          recordType: 'issuance'
        }
      });
      return;
    }

    // Returns moved out of Store Activities into standalone routes.
    if (
      state.stayOnStoreReturns ||
      (t === 3 && recordType === 'issuance') ||
      (t === 2 && recordType === 'issuance' && state.stayOnStoreReturns)
    ) {
      navigate('/store/returns', {
        replace: true,
        state
      });
      return;
    }

    // Inventory moved out of Store Activities into standalone routes.
    if (t === 2 && !state.receivingInventoryDomain && !state.receivingWorkflowFilter && recordType !== 'receiving') {
      navigate('/store/inventory', {
        replace: true,
        state
      });
      return;
    }

    // Receiving was removed from Store Activities — send callers to the standalone page.
    const isReceivingDeepLink =
      recordType === 'receiving' ||
      t === 2 && (state.receivingInventoryDomain || state.receivingWorkflowFilter || recordType === 'receiving') ||
      Boolean(state.receivingInventoryDomain) ||
      Boolean(state.receivingWorkflowFilter);

    // Legacy callers that still open Receiving via tab index 2 (former Receiving tab).
    if (recordType === 'receiving' || state.receivingInventoryDomain || state.receivingWorkflowFilter || t === 2) {
      const looksLikeReceiving =
        recordType === 'receiving' ||
        state.receivingInventoryDomain ||
        state.receivingWorkflowFilter ||
        (t === 2 && recordType == null && !state.procurement);
      if (looksLikeReceiving) {
        navigate('/receiving-and-inspection', {
          replace: true,
          state
        });
        return;
      }
    }

    // Procurement moved to standalone /procurement page.
    if (state.procurement || t === 6 || t === 4) {
      const looksLikeProcurement =
        Boolean(state.procurement) ||
        t === 6 ||
        (t === 4 && recordType !== 'reconciliation' && !state.loanTab && !state.stayOnStoreReturns);
      if (looksLikeProcurement) {
        navigate('/procurement', {
          replace: true,
          state
        });
        return;
      }
    }

    // Silence unused for lint while keeping the clarification block above readable.
    void isReceivingDeepLink;

    if (typeof t === 'number') {
      // Legacy tab indices included Inventory (2), Returns (3), and Procurement (4/6).
      let mappedTab = t;
      if (t >= 7) mappedTab = t - 3;
      else if (t >= 5) mappedTab = t - 1;
      else if (t >= 4) mappedTab = t - 2;
      else if (t === 3) mappedTab = STORE_TAB.RECONCILIATION;
      setTabValue(mappedTab);
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const state = location.state;
    setLinkState({ recordId: state?.recordId || null, recordType: state?.recordType || null });
  }, [location.state]);

  const hasRoleAccess = roleMatches(user?.role, [
    ROLES.STORE_EXECUTIVE,
    ROLES.STORE_INSPECTOR,
    ROLES.CA_MANAGER,
    ROLES.MRO_MANAGER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.PLANNING_MANAGER,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ]);
  const hasAccess = hasRoleAccess || hasGrantModuleAccess(viewGrants, 'store');

  const canViewStoreTab = (tabIndex) => {
    if (hasRoleAccess) return true;
    const tabKey = STORE_TAB_KEYS[tabIndex];
    return hasGrantTabAccess(viewGrants, 'store', tabKey);
  };

  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          You do not have permission to access Store Activities.
        </Typography>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Store Activities
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage work order requirements, reconciliation, tool control, parts lookup, access logging, and loan items
      </Typography>
      <WorkflowGuide variant="storeActivities" />

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {canViewStoreTab(STORE_TAB.DASHBOARD) && <Tab label="Dashboard" />}
          {canViewStoreTab(STORE_TAB.INTENT) && <Tab label="INTENT Request" />}
          {canViewStoreTab(STORE_TAB.RECONCILIATION) && <Tab label="Reconciliation" />}
          {canViewStoreTab(STORE_TAB.TOOL_CONTROL) && <Tab label="Tool Control" />}
          {canViewStoreTab(STORE_TAB.PARTS) && <Tab label="Parts" />}
          {canViewStoreTab(STORE_TAB.ACCESS_LOGS) && <Tab label="Access Logs" />}
          {canViewStoreTab(STORE_TAB.LOAN_ITEMS) && <Tab label="Loan Items" />}
          {canViewStoreTab(STORE_TAB.WORK_ORDER_REQUIREMENTS) && <Tab label="Work Order Requirements" />}
        </Tabs>
      </Paper>

      <Box>
        {tabValue === STORE_TAB.DASHBOARD && canViewStoreTab(STORE_TAB.DASHBOARD) && <StoreDashboard />}
        {tabValue === STORE_TAB.INTENT && canViewStoreTab(STORE_TAB.INTENT) && <StoreWorkOrderRequests />}
        {tabValue === STORE_TAB.WORK_ORDER_REQUIREMENTS && canViewStoreTab(STORE_TAB.WORK_ORDER_REQUIREMENTS) && <StoreWorkOrderRequirements />}
        {tabValue === STORE_TAB.RECONCILIATION && canViewStoreTab(STORE_TAB.RECONCILIATION) && (
          <ReconciliationModule
            recordId={linkState.recordType === 'reconciliation' ? linkState.recordId : null}
            recordType="reconciliation"
          />
        )}
        {tabValue === STORE_TAB.TOOL_CONTROL && canViewStoreTab(STORE_TAB.TOOL_CONTROL) && <ToolControl />}
        {tabValue === STORE_TAB.PARTS && canViewStoreTab(STORE_TAB.PARTS) && <StorePartsTab />}
        {tabValue === STORE_TAB.ACCESS_LOGS && canViewStoreTab(STORE_TAB.ACCESS_LOGS) && <AccessLogModule />}
        {tabValue === STORE_TAB.LOAN_ITEMS && canViewStoreTab(STORE_TAB.LOAN_ITEMS) && <LoanItemsModule />}
      </Box>
    </Box>
  );
};

export default Store;
